import { apiFetch, readApiJson } from '@/lib/api-fetch'
import {
  EnqueueOperationRequestSchema,
  OperationItemPageSchema,
  OperationPageSchema,
  OperationSchema,
  type EnqueueOperationRequest,
  type Operation,
  type OperationItem,
  type OperationItemPage,
  type OperationItemState,
  type OperationListScope,
  type OperationPage,
} from '@/lib/api/schemas/operations.schema'

type PageQuery = { limit?: number; cursor?: string | null }

function operationPath(id: string): string {
  return `/api/operations/${encodeURIComponent(id)}`
}

function pageQuery(query: URLSearchParams, { limit, cursor }: PageQuery): URLSearchParams {
  if (limit !== undefined) query.set('limit', String(limit))
  if (cursor) query.set('cursor', cursor)
  return query
}

export async function listOperations(
  { state, ...page }: PageQuery & { state: OperationListScope },
  signal?: AbortSignal,
): Promise<OperationPage> {
  const query = pageQuery(new URLSearchParams({ state }), page)
  return OperationPageSchema.parse(await readApiJson(await apiFetch(`/api/operations?${query.toString()}`, { signal })))
}

export async function getOperation(id: string, signal?: AbortSignal): Promise<Operation> {
  return OperationSchema.parse(await readApiJson(await apiFetch(operationPath(id), { signal })))
}

export async function listOperationItems(
  id: string,
  { state, ...page }: PageQuery & { state?: OperationItemState } = {},
  signal?: AbortSignal,
): Promise<OperationItemPage> {
  const query = pageQuery(new URLSearchParams(state ? { state } : {}), page).toString()
  const suffix = query.length > 0 ? `?${query}` : ''
  return OperationItemPageSchema.parse(await readApiJson(await apiFetch(`${operationPath(id)}/items${suffix}`, { signal })))
}

const ITEM_PAGE_LIMIT = 200
const ITEM_PAGE_CAP = 50

export async function listAllOperationItems(id: string, signal?: AbortSignal): Promise<OperationItem[]> {
  const items: OperationItem[] = []
  let cursor: string | null = null
  for (let page = 0; page < ITEM_PAGE_CAP; page += 1) {
    const result: OperationItemPage = await listOperationItems(id, { limit: ITEM_PAGE_LIMIT, cursor }, signal)
    items.push(...result.items)
    cursor = result.nextCursor
    if (cursor === null) break
  }
  return items
}

export async function enqueueOperation(request: EnqueueOperationRequest): Promise<Operation> {
  const response = await apiFetch('/api/operations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(EnqueueOperationRequestSchema.parse(request)),
  })
  return OperationSchema.parse(await readApiJson(response))
}

export async function cancelOperation(id: string): Promise<Operation> {
  const response = await apiFetch(`${operationPath(id)}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  return OperationSchema.parse(await readApiJson(response))
}
